<template>
  <div class="saved-filters">
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-md font-medium">Saved Filters</h3>
      <div class="flex gap-2">
        <button 
          v-if="hasActiveCustomPreset"
          @click="clearActiveCustomPreset"
          class="text-xs text-primary hover:underline"
        >
          Clear
        </button>
        <button 
          v-if="savedPresets.length > 0"
          @click="showManageModal = true"
          class="text-xs text-primary hover:underline"
        >
          Manage
        </button>
      </div>
    </div>
    
    <div v-if="savedPresets.length > 0" class="space-y-2">
      <div v-for="preset in savedPresets" :key="preset.id" class="flex items-center justify-between">
        <button
          class="flex items-center space-x-2 text-sm hover:text-primary"
          @click="applyPreset(preset.id)"
        >
          <ChevronRightIcon class="w-4 h-4" />
          <span>{{ preset.name }}</span>
        </button>
        <button
          class="text-sm text-destructive hover:text-destructive/80"
          @click="deletePreset(preset.id)"
        >
          <TrashIcon class="w-4 h-4" />
        </button>
      </div>
    </div>
    
    <div v-else class="text-center py-2">
      <p class="text-sm text-muted-foreground">No saved filters</p>
    </div>
    
    <button 
      v-if="hasActiveFilters"
      @click="showSaveModal = true"
      class="w-full mt-2 flex items-center justify-center gap-1 p-2 rounded-md border border-dashed border-border hover:bg-accent/50 transition-colors"
    >
      <PlusIcon class="h-4 w-4" />
      <span class="text-sm">Save Current Filters</span>
    </button>
    
    <!-- Save Filter Modal -->
    <Dialog v-model:open="showSaveModal">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Filter Combination</DialogTitle>
        </DialogHeader>
        <div class="mb-4">
          <label for="filter-name" class="block text-sm font-medium mb-1">Filter Name</label>
          <Input 
            id="filter-name"
            v-model="newFilterName"
            type="text"
            placeholder="e.g., My Perfect Car"
          />
        </div>
        <div class="mb-4">
          <label for="filter-description" class="block text-sm font-medium mb-1">Description (Optional)</label>
          <Input 
            id="filter-description"
            v-model="newFilterDescription"
            type="text"
            placeholder="e.g., My ideal car configuration"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" @click="showSaveModal = false">Cancel</Button>
          <Button 
            @click="saveCurrentFilters"
            :disabled="!newFilterName.trim()"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    <!-- Manage Filters Modal -->
    <Dialog v-model:open="showManageModal">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Saved Filters</DialogTitle>
        </DialogHeader>
        <div class="max-h-60 overflow-y-auto">
          <div 
            v-for="preset in savedPresets"
            :key="preset.id"
            class="flex items-center justify-between p-2 border-b border-border last:border-0"
          >
            <div>
              <span class="font-medium">{{ preset.name }}</span>
              <p v-if="preset.description" class="text-sm text-muted-foreground">{{ preset.description }}</p>
            </div>
            <button 
              @click="deletePreset(preset.id)"
              class="text-destructive hover:text-destructive/80"
              aria-label="Delete filter"
            >
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button @click="showManageModal = false">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { ChevronRightIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { useVehiclesStore } from '~/stores/vehicles'
import { useFilters } from '~/layers/base/composables/useFilters'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/layers/base/components/ui/dialog'
import { Button } from '~/layers/base/components/ui/button'
import { Input } from '~/layers/base/components/ui/input'

const emit = defineEmits<{
  (e: 'filter-preset-apply', presetId: string): void
}>()

const vehiclesStore = useVehiclesStore()
const { trackFilterInteraction, synchronizeFilterParams, shouldDisplayFilter } = useFilters()

const showSaveModal = ref(false)
const showManageModal = ref(false)
const newFilterName = ref('')
const newFilterDescription = ref('')

// --- Local State for Saved Presets ---
const savedPresets = ref<any[]>([])
const STORAGE_KEY = 'saved_vehicle_filters'

// --- Functions to manage local storage --- 
const loadPresets = () => {
  if (process.client) {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        savedPresets.value = JSON.parse(stored)
      } catch (e) {
        console.error("Error parsing saved presets from localStorage:", e)
        savedPresets.value = []
      }
    }
  }
}

const savePresets = () => {
  if (process.client) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedPresets.value))
    } catch (e) {
      console.error("Error saving presets to localStorage:", e)
    }
  }
}

// --- Computed Properties --- 

// Get only custom presets from local state
const customPresets = computed(() => {
  return savedPresets.value.filter((preset: any) => preset.id.startsWith('custom_'))
})

// Check if there are active filters
const hasActiveFilters = computed(() => {
  return Object.values(vehiclesStore.activeFilters).some(val => 
    val !== null && val !== undefined && val !== '' && (!Array.isArray(val) || val.length > 0)
  )
})

// Check if a preset is currently active
const isPresetActive = (preset: any): boolean => {
  if (!preset?.filters) return false
  return Object.entries(preset.filters).every(([filterName, presetValue]) => {
    const activeValue = vehiclesStore.activeFilters[filterName]
    
    if (activeValue === undefined || activeValue === null) {
      return presetValue === null || presetValue === undefined || (Array.isArray(presetValue) && presetValue.length === 0)
    }
    if (Array.isArray(presetValue)) {
      let activeArray: string[] = Array.isArray(activeValue) ? activeValue : []
      if (presetValue.length !== activeArray.length) return false
      const presetSet = new Set(presetValue.map(v => String(v).toLowerCase()))
      const activeSet = new Set(activeArray.map(v => String(v).toLowerCase()))
      return presetValue.every(val => activeSet.has(String(val).toLowerCase()))
    } else if (typeof presetValue === 'object' && presetValue !== null && 'min' in presetValue && 'max' in presetValue) {
      if (!(typeof activeValue === 'object' && activeValue !== null && 'min' in activeValue && 'max' in activeValue)) return false
      return activeValue.min === presetValue.min && activeValue.max === presetValue.max
    } else {
      let currentActiveValue = activeValue
      if (typeof activeValue === 'object' && activeValue !== null && 'value' in activeValue) {
        currentActiveValue = activeValue.value
      }
      return String(currentActiveValue) === String(presetValue)
    }
  })
}

// Check if any custom preset is currently active
const hasActiveCustomPreset = computed(() => {
  return savedPresets.value.some((preset: any) => isPresetActive(preset))
})

// --- Methods --- 

// Save current filter state as a new preset
const saveCurrentFilters = () => {
  const sanitizedFilters: Record<string, any> = {}
  Object.entries(vehiclesStore.activeFilters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)) {
      sanitizedFilters[key] = value
    }
  })
  
  const newPreset: any = {
    id: `custom_${Date.now()}`,
    name: newFilterName.value,
    description: newFilterDescription.value || '',
    filters: sanitizedFilters
  }
  
  savedPresets.value.push(newPreset)
  savePresets()
  
  newFilterName.value = ''
  newFilterDescription.value = ''
  showSaveModal.value = false
}

// Apply a saved preset
const applyPreset = (presetId: string) => {
  const preset = savedPresets.value.find((p: any) => p.id === presetId)
  if (!preset) return
  
  vehiclesStore.applyFilters(preset.filters)
}

// Delete a saved preset
const deletePreset = (presetId: string) => {
  savedPresets.value = savedPresets.value.filter((p: any) => p.id !== presetId)
  savePresets()
}

// Clear active custom preset
const clearActiveCustomPreset = () => {
  vehiclesStore.applyFilters({})
}

// --- Lifecycle --- 
onMounted(() => {
  loadPresets()
})
</script>
